import React, { useState, useEffect } from "react"
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    InputAdornment,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material"
import { container } from "../../infrastructure/container"
import { CreateFranchiseUsers } from "../../infrastructure/models/transport/request/CreateFranchiseUsers"
import { SessionService } from "../../infrastructure/services/session"
import { FranchiseBriefResponse } from "../../infrastructure/models/transport/response/franchiseBrief"
import { FranchiseRole } from "../../infrastructure/models/common/FranchiseRole"
import { Terminal } from "../../infrastructure/models/terminal"
import { FranchiseService } from "../../infrastructure/services/api/franchise"
import { Franchise } from "../../infrastructure/models/common/franchise"
import { Logger } from "../../infrastructure/services/logger"

/* ---- props ------------------------------------------------------ */
interface CreateFranchiseUsersProps {
    onSubmit: (data: CreateFranchiseUsers) => void
    currentUserRole: FranchiseRole
    franchiseId: string
    terminals: Terminal[]
}

const CreateFranchiseUserForm: React.FC<CreateFranchiseUsersProps> = ({ onSubmit, currentUserRole, franchiseId, terminals }) => {

    const sessionService = container.resolve<SessionService>("sessionService")
    const franchiseService = container.resolve<FranchiseService>("franchiseService")
    const logger = container.resolve<Logger>("logger")

    // get that franchise from session
    // Local list that the UI will render from
    const [terminalList, setTerminalList] = useState<Terminal[]>(terminals ?? [])
    const [apiLoading, setApiLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // If parent updates terminals, reflect it
    useEffect(() => {
        setTerminalList(terminals ?? [])
    }, [terminals])

    // Fallback: if parent didn't pass terminals, fetch full franchise (once)
    useEffect(() => {
        if (terminalList.length > 0 || !franchiseId) return
        let alive = true;
        (async () => {
            try {
                setApiLoading(true)
                // Adjust to your real method name:
                // const f = await franchiseService.getFranchise(franchiseId)
                const f = await franchiseService.getFranchise(franchiseId)
                if (alive) setTerminalList(f?.terminals ?? [])
            } catch (e) {
                logger.error("Failed to load franchise terminals", e)
                if (alive) setError(e instanceof Error ? e.message : "Failed to load terminals")
            } finally {
                if (alive) setApiLoading(false)
            }
        })()
        return () => { alive = false }
    }, [franchiseId, terminalList.length, franchiseService, logger])

    /* ----- form state --------------------------------------------- */
    const [formData, setFormData] = useState<CreateFranchiseUsers>({
        franchiseId: "",
        firstname: "",
        lastname: "",
        phone: {
            primary: "+91",   // keep it as Phone object, not string
            secondary: ""
        },
        email: "",
        terminals: [],
        address: { street: "", city: "", state: "", postalCode: "" },
        role: FranchiseRole.ADMIN // default role
    })

    // Keep franchiseId in sync
    useEffect(() => {
        setFormData(p => ({ ...p, franchiseId }))
    }, [franchiseId])

    const toggleTerminal = (id: string) => {
        setFormData(prev => ({
            ...prev,
            terminals: prev.terminals.includes(id)
                ? prev.terminals.filter(tid => tid !== id)
                : [...prev.terminals, id],
        }))
    }

    /* ----- checkbox options --------------------------------------- */
    const [franchiseOptions, setFranchiseOptions] = useState<
        { id: string, name: string }[]
    >([])

    const [franchise, setFranchise] = useState<Franchise | null>(null)
    const [open, setOpen] = useState(false)
    const [tabIndex, setTabIndex] = useState(0)
    const [cardView, setCardView] = useState(true)

    const getVisibleRoles = (): { label: string, value: number }[] => {
        return Object.keys(FranchiseRole)
            .filter(k => isNaN(Number(k))) // only string keys, not reverse numeric mappings
            .map(k => ({
                label: k.replace(/_/g, " "),
                value: FranchiseRole[k as keyof typeof FranchiseRole]
            }))
    }

    // tie this user to the franchise being viewed
    useEffect(() => {
        if (franchiseId) {
            setFormData((p) => ({ ...p, franchiseId }))
        }
    }, [franchiseId])

    const toggleFranchise = (id: string) => {
        setFormData((prev) => ({
            ...prev,
            terminals: prev.terminals.includes(id)
                ? prev.terminals.filter((fid) => fid !== id)
                : [...prev.terminals, id],
        }))
    }

    /* ----- load franchises from SessionService -------------------- */
    useEffect(() => {
        const sessionService = container.resolve<SessionService>("sessionService")

        const brief: FranchiseBriefResponse[] = sessionService.franchiseBrief
        const opts =
            brief.length > 0
                ? brief.map(b => ({ id: b.franchiseId, name: b.name }))
                : sessionService.terminals.map((f: Terminal) => ({
                    id: f.terminalId,
                    name: f.name,
                }))

        setFranchiseOptions(opts)
    }, [])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        const safeValue = value ?? ""   // ensure empty string, never undefined

        if (name === "role") {
            setFormData(p => ({ ...p, role: Number(safeValue) as FranchiseRole }))
        } else if (name.startsWith("address.")) {
            const key = name.split(".")[1] as keyof typeof formData.address
            setFormData(p => ({ ...p, address: { ...p.address, [key]: safeValue } }))
        } else {
            setFormData(p => ({ ...p, [name]: safeValue }))
        }
    }

    return (
        <Box
            sx={{
                p: 4,
                border: "1px solid #ccc",
                borderRadius: "8px",
                backgroundColor: "#fff",
            }}
        >
            <Typography variant="h5" gutterBottom>
                Add Franchise User
            </Typography>

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    {/* firstname */}
                    <TextField
                        fullWidth
                        label="First Name"
                        name="firstname"
                        value={formData.firstname}
                        onChange={handleChange}
                    />
                </Grid>

                <Grid size={{ xs: 6, sm: 6 }}>
                    {/* lastname */}
                    <TextField
                        fullWidth
                        label="Last Name"
                        name="lastname"
                        value={formData.lastname}
                        onChange={handleChange}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 4 }}>
                    {/* role dropdown */}
                    <TextField
                        select
                        fullWidth
                        label="Role"
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                    >
                        {getVisibleRoles().map(role => (
                            <MenuItem key={role.value} value={role.value}>
                                {role.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                <Grid size={{ xs: 4, sm: 4 }}>
                    <TextField
                        fullWidth
                        required
                        label="User Primary Number"
                        name="phone"
                        value={formData.phone.primary.replace("+91", "")}
                        onChange={(e) => {
                            const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10)
                            setFormData((prev) => ({
                                ...prev,
                                phone: {
                                    ...prev.phone,
                                    primary: `+91${digitsOnly}`
                                }
                            }))
                        }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">+91</InputAdornment>
                        }}
                        inputProps={{
                            maxLength: 10,
                            pattern: "\\d{10}"
                        }}
                    />
                </Grid>

                <Grid size={{ xs: 4, sm: 4 }}>
                    <TextField
                        fullWidth
                        label="User Alternate Number"
                        name="phone"
                        value={formData.phone.secondary.replace("+91", "")}
                        onChange={(e) => {
                            const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10)
                            setFormData((prev) => ({
                                ...prev,
                                phone: {
                                    ...prev.phone,
                                    secondary: `+91${digitsOnly}`
                                }
                            }))
                        }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">+91</InputAdornment>
                        }}
                        inputProps={{
                            maxLength: 10,
                            pattern: "\\d{10}"
                        }}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 12 }}>
                    {/* Email */}
                    <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                    />
                </Grid>

                {/* Terminals of the selected franchise */}
                <Grid size={{ xs: 12 }}>
                    <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2, position: "relative" }}>
                        <Typography variant="subtitle1" sx={{ position: "absolute", top: "-12px", left: "16px", backgroundColor: "#fff", px: 1 }}>
                            Select Terminals
                        </Typography>

                        {apiLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                                <CircularProgress />
                            </Box>
                        ) : terminalList.length > 0 ? (
                            <Grid container spacing={1}>
                                {terminalList
                                    .filter(t => t.status !== "Inactive")                 // optional: only active
                                    .map((term) => (
                                        <Grid key={term.terminalId} size={{ xs: 12, sm: 4 }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData.terminals.includes(term.terminalId)}
                                                        onChange={() => toggleTerminal(term.terminalId)}
                                                    />
                                                }
                                                label={term.name}                               // terminal name
                                            />
                                        </Grid>
                                    ))}
                            </Grid>
                        ) : (
                            <Typography sx={{ px: 2, py: 1 }}>No terminals for this franchise.</Typography>
                        )}

                        {error && (
                            <Typography sx={{ mt: 1 }} color="error">{error}</Typography>
                        )}
                    </Box>
                </Grid>

                {/* address */}
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        label="Street"
                        name="address.street"
                        value={formData.address.street}
                        onChange={handleChange}
                    />
                </Grid>

                <Grid size={{ xs: 5 }}>
                    <TextField
                        fullWidth
                        label="City"
                        name="address.city"
                        value={formData.address.city}
                        onChange={handleChange}
                    />
                </Grid>

                <Grid size={{ xs: 5 }}>
                    <TextField
                        fullWidth
                        label="State"
                        name="address.state"
                        value={formData.address.state}
                        onChange={handleChange}
                    />
                </Grid>

                <Grid size={{ xs: 2 }}>
                    <TextField
                        fullWidth
                        label="Postal Code"
                        name="address.postalCode"
                        value={formData.address.postalCode}
                        onChange={handleChange}
                        inputProps={{ maxLength: 6 }}
                    />
                </Grid>
            </Grid>

            <Button
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                onClick={() => {
                    console.log("Submitting:", formData) // <-- Add this
                    onSubmit(formData)
                }}
            >
                Submit
            </Button>
        </Box>
    )
}

export default CreateFranchiseUserForm